/**
 * ACHIEVEMENT MODAL
 *
 * Beautiful modal that displays when user unlocks an achievement
 * Includes confetti animation and celebration effects
 */

import React, { useEffect } from 'react';
import { X, Trophy, Star } from 'lucide-react';
// @ts-ignore
import confetti from 'canvas-confetti';
import { Achievement, getRarityColor } from '../../constants/gamification';

interface AchievementModalProps {
  achievement: Achievement;
  points: number;
  onClose: () => void;
  autoCloseDelay?: number; // Auto-close after X milliseconds (default: 5000)
}

const AchievementModal: React.FC<AchievementModalProps> = ({
  achievement,
  points,
  onClose,
  autoCloseDelay = 5000
}) => {
  useEffect(() => {
    // Trigger confetti animation
    fireConfetti();

    // Auto-close after delay
    const timer = setTimeout(() => {
      onClose();
    }, autoCloseDelay);

    return () => clearTimeout(timer);
  }, [autoCloseDelay, onClose]);

  const fireConfetti = () => {
    const duration = 3000;
    const animationEnd = Date.now() + duration;
    const defaults = { startVelocity: 30, spread: 360, ticks: 60, zIndex: 9999 };

    const randomInRange = (min: number, max: number) => {
      return Math.random() * (max - min) + min;
    };

    const interval = setInterval(() => {
      const timeLeft = animationEnd - Date.now();

      if (timeLeft <= 0) {
        return clearInterval(interval);
      }

      const particleCount = 50 * (timeLeft / duration);

      // Fire confetti from different positions
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.1, 0.3), y: Math.random() - 0.2 }
      });
      confetti({
        ...defaults,
        particleCount,
        origin: { x: randomInRange(0.7, 0.9), y: Math.random() - 0.2 }
      });
    }, 250);
  };

  const rarityGradients: Record<Achievement['rarity'], string> = {
    common: 'from-gray-400 to-gray-600',
    uncommon: 'from-green-400 to-green-600',
    rare: 'from-blue-400 to-blue-600',
    epic: 'from-purple-400 to-purple-600',
    legendary: 'from-yellow-400 to-yellow-600'
  };

  const rarityLabels: Record<Achievement['rarity'], string> = {
    common: 'Common',
    uncommon: 'Uncommon',
    rare: 'Rare',
    epic: 'Epic',
    legendary: 'Legendary'
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-bounce-in">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
        >
          <X className="w-6 h-6" />
        </button>

        {/* Header with gradient */}
        <div className={`bg-gradient-to-r ${rarityGradients[achievement.rarity]} p-8 text-white text-center relative overflow-hidden`}>
          {/* Animated background pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 animate-pulse">
              {[...Array(20)].map((_, i) => (
                <Star
                  key={i}
                  className="absolute text-white"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    width: `${Math.random() * 20 + 10}px`,
                    height: `${Math.random() * 20 + 10}px`,
                    animationDelay: `${Math.random() * 2}s`
                  }}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="relative z-10">
            <div className="text-6xl mb-4 animate-bounce">{achievement.emoji}</div>
            <h2 className="text-3xl font-bold mb-2">🎉 Achievement Unlocked!</h2>
            <div className="inline-block px-4 py-1 bg-white bg-opacity-20 rounded-full text-sm font-semibold">
              {rarityLabels[achievement.rarity]}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-2 text-center">
            {achievement.title}
          </h3>
          <p className="text-gray-600 text-center mb-6">
            {achievement.description}
          </p>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-red-50 to-red-100 rounded-xl p-4 text-center">
              <Trophy className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-red-900">+{points}</div>
              <div className="text-xs text-red-700">Points</div>
            </div>
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 text-center">
              <Star className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-900">+{points}</div>
              <div className="text-xs text-blue-700">XP</div>
            </div>
          </div>

          {/* Action Button */}
          <button
            onClick={onClose}
            className="w-full bg-gradient-to-r from-red-600 to-red-800 text-white py-3 rounded-lg font-semibold hover:from-red-700 hover:to-red-900 transition-all shadow-lg hover:shadow-xl"
          >
            Awesome!
          </button>
        </div>
      </div>

      <style>{`
        @keyframes bounce-in {
          0% {
            transform: scale(0.3);
            opacity: 0;
          }
          50% {
            transform: scale(1.05);
          }
          70% {
            transform: scale(0.9);
          }
          100% {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-bounce-in {
          animation: bounce-in 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55);
        }
      `}</style>
    </div>
  );
};

export default AchievementModal;
